import { useMutation, useQuery } from "@tanstack/react-query";
import { isAxiosError } from "axios";

import { backofficeClient } from "@/lib/api/backoffice-client";

/** integrations service credentials list, via the backoffice proxy. */
const CREDENTIALS_PATH = "/proxy/integrations/api/v1/credentials";
/** Mirrors backend skill custom-script-deployment. */
const TRIGGER_SCRIPTS_PATH =
  "/proxy/device-management/api/v1/custom-scripts/trigger-integration-sync";
/** Mirrors backend skill employee-sync (all of the tenant's connections). */
const EMPLOYEE_SYNC_PATH = "/proxy/employees-sync/api/v1/employees-sync/tenant";

/**
 * The only MDM providers device-management can push scripts to; the
 * custom-script-deployment skill skips every other MDM.
 */
export const SCRIPT_MDM_PROVIDERS = ["jamf", "jumpcloud", "intune", "kandji"];

/**
 * backoffice-api gives up on a proxied call after 30s while the service keeps
 * working, so a gateway error that late means "still running", not "failed".
 */
const PROXY_TIMEOUT_MS = 25_000;

export interface MdmCredential {
  id: string;
  name: string;
  provider: string;
  label: string | null;
}

/** How a long-running trigger ended, as far as the browser can tell. */
export type TriggerOutcome<T = unknown> =
  | { outcome: "completed"; data: T }
  | { outcome: "still-running" };

async function runTrigger<T>(
  call: () => Promise<{ data: T }>,
): Promise<TriggerOutcome<T>> {
  const started = Date.now();
  try {
    const { data } = await call();
    return { outcome: "completed", data };
  } catch (error) {
    const status = isAxiosError(error) ? error.response?.status : undefined;
    if (
      status !== undefined &&
      status >= 500 &&
      Date.now() - started >= PROXY_TIMEOUT_MS
    ) {
      return { outcome: "still-running" };
    }
    throw error;
  }
}

export function useScriptMdmCredentials(tenantId: string) {
  return useQuery({
    queryKey: ["solution-engineer", "mdm-credentials", tenantId],
    queryFn: async () => {
      const { data } = await backofficeClient.get<{
        credentials: MdmCredential[];
      }>(CREDENTIALS_PATH, {
        params: { tenant_id: tenantId, category: "mdm" },
      });
      return data.credentials.filter((c) =>
        SCRIPT_MDM_PROVIDERS.includes(c.provider),
      );
    },
    enabled: Boolean(tenantId),
  });
}

/** Pushes the tenant's custom scripts, peripheral scans included, to one MDM. */
export function useDeployPeripheralScripts(tenantId: string) {
  return useMutation({
    mutationFn: (integrationId: string) =>
      runTrigger(() =>
        backofficeClient.post(
          TRIGGER_SCRIPTS_PATH,
          { integration_id: integrationId },
          { params: { tenant_id: tenantId } },
        ),
      ),
  });
}

/** Re-pulls employees from every HRIS and IDP the tenant has connected. */
export function useResyncEmployees(tenantId: string) {
  return useMutation({
    mutationFn: () =>
      runTrigger(() =>
        backofficeClient.post(
          `${EMPLOYEE_SYNC_PATH}/${tenantId}`,
          undefined,
          { params: { tenant_id: tenantId } },
        ),
      ),
  });
}
